"use client"

import React, { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { receiveMessageFromContainer } from '@/components/C1Opener/ReceiveMessageUtils';
import {
    fullScreen,
    init,
    InitData,
    ready,
    sendTermination,
    showNextActivity,
    setControlButton,
    sendIframeEvent,
    showExitConfirmationModal
} from '@/components/C1Opener/SendMessageUtils';
import { sendLaunchedStatement } from '@/components/C1Opener/statements/launched';
import { MessagingChannel } from 'jschannel';
import { useIsC1Integrated } from '@/components/C1Integration/useIsC1Integrated';
import { Control, CheckResult, LaunchedPageId, ControlOptions } from '@/components/C1Opener/OpenerTypes';
import { sendCompletedStatement } from '@/components/C1Opener/statements/completed';
import { sendConsumedStatement } from '@/components/C1Opener/statements/consumed';
import { sendExperiencedStatement } from '@/components/C1Opener/statements/experienced';
import { sendProgressedStatement } from '@/components/C1Opener/statements/progressed';
import { sendQualifiedStatement } from '@/components/C1Opener/statements/qualified';
import { sendReceivedStatement } from '@/components/C1Opener/statements/received';
import { OpenerInteractionType } from '@/components/C1Opener/OpenerInteractionType';
import { AnalysisResponse } from '@/api/analyzeTranscript';
import { usePageInfo } from '@/components/C1Opener/PageCountHook';

export const OpenerContext = createContext<null | OpenerInteractionType>(null);

export const OpenerCommunicatorContextProvider: React.FC<{ children: ReactNode }> = ({children}) => {
    const [openerInteraction, setOpenerInteraction] = useState<OpenerInteractionType | null>(null);
    const [channel, setChannel] = useState<MessagingChannel>();
    const [initData, setInitData] = useState<InitData>();
    const isC1Integrated = useIsC1Integrated();
    const [hasGigyaSessionLoaded, setHasGigyaSessionLoaded] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const { updateCurrentPage, totalPages, currentPage } = usePageInfo();
    const currentPageWrapper = useRef(currentPage);

    useEffect(() => {
        currentPageWrapper.current = currentPage;
    }, [currentPage]);

    /**
     * A map of callbacks called when control buttons are clicked. The keys are
     * - readConversation
     * - continueConversation
     */
    const controlsCallbackMap = useRef<Record<Control, (() => void) | undefined>>({
        readConversation: undefined,
        continueConversation: undefined,
        closeConversation: undefined
    });

    /**
     * A callback to be invoked when the container requests an exit from the conversation with AI.
     */
    const exitConversationCallback = useRef<((data: {value: boolean}) => void) | undefined>(undefined);

    // Set up channel
    useEffect(() => {
        if(! isC1Integrated   ) {
            return;
        }

        (async () => {
            const Channel = await import("jschannel");

            const channel = Channel.build({
                window: window.parent,
                origin: '*',
                scope: 'default', // 'wrapper-nonstop',
                onReady: () => setChannel(channel)
            });
        })()
    }, [isC1Integrated]);

    useEffect(() => {
        if(isClosing && channel) {
            sendTermination(channel);
        }
    }, [isClosing, channel]);

    // Listen for iframe events and send to container
    useEffect(() => {
        if (!channel || !window || !window.addEventListener) {
            return;
        }

        const eventsToBind = ['unload', 'beforeunload', 'click', 'touchstart'];

        const eventHandlers = eventsToBind.map(eventType => {
            const handler = (eventData: Event) => {
                sendIframeEvent(channel, eventType, eventData);
            };
            window.addEventListener(eventType, handler);
            return { eventType, handler };
        });

        return () => {
            eventHandlers.forEach(({ eventType, handler }) => {
                window.removeEventListener(eventType, handler);
            });
        };
    }, [channel]);

    // Listen for messages from container
    useEffect(() => {
        if(! channel) {
            return;
        }

        try {
            receiveMessageFromContainer(channel, {
                close: trans => {
                    // Kill everything and send any final messages
                    trans.delayReturn(true);
                    setIsClosing(true);
                },
                totalScreens: trans => {
                    const message = totalPages;
                    trans.complete(message);
                },
                currentScreen: trans => {
                    const message = currentPageWrapper.current;
                    trans.complete(message);
                },
                readConversation: () => {
                    if (controlsCallbackMap.current.readConversation) {
                        controlsCallbackMap.current.readConversation();
                        // Reset callback after use
                        controlsCallbackMap.current.readConversation = undefined;
                    }
                },
                continueConversation: () => {
                    if (controlsCallbackMap.current.continueConversation) {
                        controlsCallbackMap.current.continueConversation();
                        // Reset callback after use
                        controlsCallbackMap.current.continueConversation = undefined;
                    }
                },
                closeConversation: () => {
                    if (controlsCallbackMap.current.closeConversation) {
                        controlsCallbackMap.current.closeConversation();
                        // Reset callback after use
                        controlsCallbackMap.current.closeConversation = undefined;
                    }
                },
                exitConversation: (trans, payload) => {
                    if (exitConversationCallback.current) {
                        // Extract the data payload from the params
                        const data = (payload as {data: {value: boolean}})?.data;
                        exitConversationCallback.current(data);
                        exitConversationCallback.current = undefined;
                    }
                    trans.complete(true);
                }
            });
        } catch(err) {
            console.error('Unable to setup receive due to error', err);
        }

        init(channel)
            .then(data => {
                setInitData(data);
            })
            .catch(err => console.error('Error calling init', err));
    }, [channel, setInitData, setIsClosing, totalPages]);

    /**
     * Send message to the container that the content is ready.
     * Now the container can hide the loader and show the content.
     */
    const sendReady = useCallback(() => {
        if(! channel) {
            throw new Error('Attempt to mark ready called before js channel available');
        }
        ready(channel).catch((err) =>
            console.error("Error calling ready", err)
        );
    }, [channel]);

    useEffect(() => {
        if(! channel || ! initData) {
            return;
        }

        const contentId = initData.id;

        setOpenerInteraction({
            fullScreen: () => fullScreen(channel, true),
            endFullScreen: () => fullScreen(channel, false),
            showNextActivityButton: () => showNextActivity(channel, true),
            hideNextActivityButton: () => showNextActivity(channel, false),
            close: () => setIsClosing(true),
            hasGigyaSessionLoaded,
            setHasGigyaSessionLoaded,
            initData,
            updateCurrentPage,
            sendReady,
            canSpeakToMimi: (() => {
                return !initData.areAttemptsExhausted;
            })(),
            sendLaunched: (pageId: LaunchedPageId, title: string) => sendLaunchedStatement(channel, contentId, pageId, title),
            sendCompleted: () => sendCompletedStatement(channel, contentId),
            sendExperienced: (millisSinceLastSend: number) => sendExperiencedStatement(channel, contentId, millisSinceLastSend),
            sendTechnicalCheckResult: (checkResult: CheckResult) => sendQualifiedStatement(channel, contentId, checkResult),
            sendWarmupComplete: () => sendConsumedStatement(channel, contentId),
            sendCEFRReceived: (cefrResults: AnalysisResponse) => {
                const cefrSummary = Object.keys(cefrResults.words).map(level => {
                    return `${level} (${Object.keys(cefrResults.words[level]).length})`;
                }).join(', ');
                return sendReceivedStatement(channel, contentId, 'USER_CEFR_RESULT', cefrSummary, cefrResults);
            },
            sendUserSilentReceived: (silenceFeedback: string) => sendReceivedStatement(channel, contentId, 'USER_SILENT', silenceFeedback),
            sendUserContinued: (continueCount: number) => sendProgressedStatement(channel, contentId, continueCount),
            setControlButton: (controlButton: Control, options: ControlOptions, callback?: () => void) => {
                controlsCallbackMap.current[controlButton] = callback;
                return setControlButton(channel, controlButton, options);
            },
            showExitConfirmationModal: (options: { isVisible: boolean }, callback?: (data: {value: boolean}) => void) => {
                exitConversationCallback.current = callback;
                return showExitConfirmationModal(channel, options);
            }
        });
    }, [channel, initData, setIsClosing, hasGigyaSessionLoaded, setHasGigyaSessionLoaded, updateCurrentPage, sendReady]);

    // If isClosing is true then children are no longer rendered, allowing them to perform any required de-rendering.
    return <OpenerContext.Provider value={openerInteraction}>
        {!isClosing && children}
    </OpenerContext.Provider>;
}